import { Anchor, Button, Group, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { useForm } from '@mantine/form'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { api, getUrlString, notificationHandler } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'

import { EmailField, PasswordField } from './Fields'

interface Props {
  hideSignUp?: boolean
}

export default function Login({ hideSignUp = false }: Props) {
  // Hooks
  const router = useRouter()
  const { forgotPassword, login } = useAuth() || {}

  // Constants
  const { callbackUrl, channel, site } = router.query
  const siteSlug = getUrlString(site)
  const channelSlug = getUrlString(channel)
  const callbackUrlSlug = getUrlString(callbackUrl)

  // States
  const [loading, setLoading] = useState(false)
  const [changePassword, setChangePassword] = useState(false)
  const form = useForm({
    initialValues: {
      email: '',
      password: ''
    },
    validate: {
      email: val => (/^\S+@\S+\.\S+$/.test(val) ? null : 'E-mail inválido'),
      password: val =>
        !changePassword && val.length < 6 ? 'Senha deve conter no mínimo 6 caracteres' : null
    }
  })

  // Actions
  const handleCheckEmail = async () => {
    const response = await api.post(`/${siteSlug}/accounts/check-email/`, {
      email: form.values.email
    })
    if (response?.data?.flow === 'signup') {
      if (hideSignUp) {
        notificationHandler({
          variant: 'error',
          message:
            'Usuário não encontrado. Entre em contato com o suporte do site caso o e-mail esteja correto ou acesse com outra conta.'
        })
      } else {
        notificationHandler({
          variant: 'error',
          message: 'Usuário não encontrado. Cadastre-se agora.'
        })
        const params = new URLSearchParams()
        if (callbackUrlSlug) params.set('callbackUrl', callbackUrlSlug)
        if (channelSlug) params.set('channel', channelSlug)
        if (form.values.email) params.set('email', form.values.email)
        router.push(`/accounts/signup${params.toString() ? `?${params.toString()}` : ''}`)
      }
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    setLoading(true)

    if (changePassword) {
      try {
        const response = await forgotPassword?.({
          email: form.values.email,
          callbackUrl: callbackUrlSlug
        })
        const { status } = response || {}
        if (status === 204) {
          notificationHandler({
            variant: 'info',
            message:
              'Enviamos um link para o e-mail cadastrado. Acesse o link para resetar a senha.'
          })
        }
        form.setFieldValue('password', '')
      } catch (err: any) {
        const { response } = err
        notificationHandler({ variant: 'error', message: response?.data?.detail })
      }
    } else {
      try {
        const emailExists = await handleCheckEmail()
        if (emailExists) await login?.({ login: form.values.email, password: form.values.password })
      } catch (err: any) {
        const errorText = typeof err === 'string' ? err : err?.response?.data?.detail
        notificationHandler({
          variant: 'error',
          message: errorText || 'Ocorreu um erro ao fazer o login. Tente novamente mais tarde.'
        })
      }
    }
    setTimeout(() => {
      setLoading(false)
    }, 1000)
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack align="center">
        <Title size="h4">{changePassword ? 'Mudar senha' : 'Login'}</Title>
        <EmailField inputProps={{ ...form.getInputProps('email'), w: '100%' }} />
        {!changePassword && (
          <PasswordField inputProps={{ ...form.getInputProps('password'), w: '100%' }} />
        )}
        <Group position="apart" align="center" w="100%">
          <UnstyledButton
            onClick={() => {
              setChangePassword(prevValue => !prevValue)
              form.clearFieldError('password')
            }}>
            <Text size="sm" color="blue">
              {changePassword ? 'Voltar' : 'Esqueci a senha'}
            </Text>
          </UnstyledButton>
          <Text size="sm" hidden={hideSignUp || changePassword}>
            <Anchor
              component={Link}
              color="blue"
              href={{
                pathname: '/accounts/signup',
                query: {
                  ...(callbackUrlSlug ? { callbackUrl: callbackUrlSlug } : {}),
                  ...(channelSlug ? { channel: channelSlug } : {})
                }
              }}>
              Cadastre-se
            </Anchor>
          </Text>
        </Group>
        <Button type="submit" fullWidth color="blue" loading={loading}>
          {changePassword ? 'Enviar' : 'Login'}
        </Button>
      </Stack>
    </form>
  )
}
